import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import useMutation from "src/hooks/useMutation";
import {
  UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_MUTATION,
  CREATE_INVENTORY_TRANSACTION_MUTATION,
} from "src/constants/graphql/mutations";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import useNotification from "src/hooks/notification";
import {
  buildNonAllocatingTransactionPayload,
  getSourceRackLocationOptions,
} from "src/utils/internalWorkOrderRackLocations";

const questions = {
  staging: "Which location are you staging this item in?",
  return: "Which location are you returning this item to?",
};

const successMessages = {
  staging: "Item staged successfully",
  return: "Item returned to storage successfully",
};

export default function StagingOrReturnModal({
  open = true,
  mode = "staging",
  asset,
  item,
  internalWorkOrder,
  onClose,
  onSuccess,
}) {
  const [saving, setSaving] = useState(false);
  const sourceLocationOptions = useMemo(
    () => getSourceRackLocationOptions(asset?.rackLocations),
    [asset?.rackLocations]
  );
  const [sourceLocation, setSourceLocation] = useState(
    sourceLocationOptions.length === 1 ? sourceLocationOptions[0] : null
  );
  const [location, setLocation] = useState({
    warehouse: null,
    warehouse_locations: [],
    sub_qty: item?.quantity || 0,
  });

  const [updateItem] = useMutation(UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION);
  const [updateInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [createTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const { notifySuccess } = useNotification();

  const header = "Internal Work Order Assistant";
  const question = questions[mode];
  const requiresSourceLocation = item?.nonAllocating;
  const isDestinationValid = !!(location?.warehouse?.id && location?.warehouse_locations?.[0]?.id);
  const canSubmit =
    isDestinationValid &&
    (!requiresSourceLocation || (sourceLocationOptions.length > 0 && !!sourceLocation));

  useEffect(() => {
    if (sourceLocationOptions.length === 1) {
      setSourceLocation(sourceLocationOptions[0]);
    }
  }, [sourceLocationOptions]);

  const updateStatus = async () => {
    const statusPatch = mode === "staging" ? { pickedStatus: true } : { completedStatus: true };

    await updateItem({
      variables: {
        id: item.id,
        input: { ...statusPatch },
      },
    });
  };

  const updateParentStatusIfComplete = async () => {
    if (!internalWorkOrder?.id) return;

    const allItemsMatchTarget = internalWorkOrder.internalWorkOrderItems.every(workOrderItem => {
      if (workOrderItem.id === item.id) {
        return true;
      }

      return mode === "staging" ? workOrderItem.pickedStatus : workOrderItem.completedStatus;
    });

    if (!allItemsMatchTarget) return;

    await updateInternalWorkOrder({
      variables: {
        id: internalWorkOrder.id,
        input: {
          vaStatus: mode === "staging" ? { picked: true } : { completed: true },
        },
      },
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      let rackLocations = (asset?.rackLocations || []).map(l => ({ ...l }));
      let oldRackLocations = asset?.rackLocations || [];
      let rackLocationsChanges = [
        {
          ...location,
          sub_qty: item?.quantity || 0,
        },
      ];

      if (item?.nonAllocating) {
        ({ rackLocations, oldRackLocations, rackLocationsChanges } = buildNonAllocatingTransactionPayload({
          assetRackLocations: asset?.rackLocations,
          sourceLocationOption: sourceLocation,
          destination: location,
        }));
      } else if (rackLocations.length) {
        rackLocations[0] = { ...rackLocations[0], ...location, sub_qty: item?.quantity || 0 };
      } else {
        rackLocations.push({ ...location, sub_qty: item?.quantity || 0 });
      }

      await createTransaction({
        variables: {
          code: asset.assetId,
          quantity: 0,
          notes: "",
          senderName: "",
          carrierName: "",
          trackingNumber: "",
          orderNumber: "",
          poNumber: "",
          rackLocations,
          rackLocationsChanges,
          oldRackLocations,
          internalWorkOrderItemId: item.id,
          actionSource:
            mode === "staging" ? "internal_work_order_staging" : "internal_work_order_return",
        },
      });

      await updateStatus();
      await updateParentStatusIfComplete();

      notifySuccess(successMessages[mode]);

      onSuccess && onSuccess();
      onClose && onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} sx={{ '& .MuiDialog-paper': { width: 400 } }}>
      <DialogTitle>
        <Typography align="center" fontSize={18} fontWeight={500} color="text.primary">
          {header}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box py={4}>
          {requiresSourceLocation && (
            <Box mb={3}>
              <Typography align="center" sx={{ mb: 2 }} color="text.primary" fontSize={16}>
                Which current location are you moving this item from?
              </Typography>
              <Autocomplete
                options={sourceLocationOptions}
                value={sourceLocation}
                onChange={(_, option) => setSourceLocation(option)}
                disabled={saving || sourceLocationOptions.length === 0}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.label}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Current Location"
                    placeholder="Select current location"
                  />
                )}
              />
              {sourceLocationOptions.length === 0 && (
                <Typography color="error" variant="body2" mt={1}>
                  This non-allocating item has no current asset rack locations to move from.
                </Typography>
              )}
            </Box>
          )}
          <Typography align="center" sx={{ mb: 2 }} color="text.primary" fontSize={16}>
            {question}
          </Typography>
          <StorageLocationAutocomplete
            name="location"
            value={location}
            projectId={asset?.project?.id}
            onChange={values => setLocation(prev => ({ ...prev, ...values }))}
            disabled={saving}
            loading={false}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
